import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import TrustedBy from './TrustedBy';
import { Badge } from './ui/badge';
import { ArrowRight, Factory, Landmark, Building2, Anchor } from 'lucide-react';

const CUSTOMER_CATEGORIES = [
  {
    icon: Factory,
    title: 'Fortune 500 Industrials',
    desc: 'Refineries, automotive plants, power producers, and chemical processors who depend on uptime — Chevron, Phillips 66, Marathon, Ford, Chrysler, GenOn, Veolia.',
  },
  {
    icon: Landmark,
    title: 'Public Agencies & Utilities',
    desc: 'Municipal water districts, sanitation authorities, and public works departments operating the infrastructure that millions of Americans rely on every day.',
  },
  {
    icon: Building2,
    title: 'Commercial & Institutional',
    desc: 'Theme parks, university campuses, and major property managers who need their critical building systems maintained without interrupting operations.',
  },
  {
    icon: Anchor,
    title: 'Defense & Maritime',
    desc: 'The U.S. Navy and marine operators trust BCS for the corrosion-resistant repair systems that protect mission-critical equipment in the harshest environments.',
  },
];

const Customers = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="customers-page">
      <SEO
        title="Our Customers | Trusted by Industry Leaders Nationwide | BCS"
        description="See the industry leaders, government agencies, and Fortune 500 manufacturers nationwide who trust BCS for industrial coatings, corrosion repair, and asset life extension."
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.3),transparent_40%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(255,165,0,0.2),transparent_40%)]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <Badge className="mb-5 bg-white/20 text-white border border-white/30 hover:bg-white/20">Our Customers</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Trusted by the Nation's Leading Industrial Operators
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            BCS partners with refineries, power plants, water districts, municipalities,
            and Fortune 500 manufacturers to extend asset life and protect critical infrastructure.
          </p>
        </div>
      </section>

      {/* Opening narrative */}
      <section className="pt-16 pb-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100">
            Who We Serve
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-5">
            The Companies Who Count On Us
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed text-left">
            <p>
              The BCS customer roster reads like a who's who of the assets that keep America
              running. Refineries that fuel the nation. Power plants that light millions of homes.
              Water districts that deliver safe drinking water to entire metropolitan regions.
              Defense installations protecting national interests. Manufacturers building the cars,
              consumer goods, and equipment that touch every household.
            </p>
            <p>
              What every one of these organizations has in common is the same hard reality:
              their equipment can't fail. Downtime measured in hours can cost millions. A failed
              clarifier, a corroded pump, a leaking pipe — these aren't inconveniences, they're
              regulatory events, safety incidents, and budget catastrophes.
            </p>
            <p>
              That's why they call BCS. For over two decades we've built our reputation on showing
              up when it matters most, executing repairs that hold up under the worst conditions,
              and standing behind our work with measurable, document-backed warranties.
            </p>
            <p className="text-center text-slate-900 font-semibold text-xl pt-4">
              Here are just a few of the customers who trust BCS.
            </p>
          </div>
        </div>
      </section>

      {/* TrustedBy logos — full roster, header hidden */}
      <TrustedBy compact hideHeader />

      {/* Customer categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-100">
              Customer Profiles
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              The Sectors We Power
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From the boardroom to the boiler room, the leaders below trust BCS with the assets
              that can't go down.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {CUSTOMER_CATEGORIES.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <div
                  key={i}
                  data-testid={`customer-category-${i}`}
                  className="group p-7 bg-slate-50 hover:bg-white border border-slate-200 hover:border-blue-900 rounded-2xl hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-indigo-800 text-white shadow-md group-hover:scale-110 transition-transform">
                      <Icon size={22} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{cat.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{cat.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Loyalty + Quality */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Customer Loyalty and BCS Quality
            </h2>
          </div>
          <div className="space-y-5 text-lg text-gray-700 leading-relaxed">
            <p>
              Procurement teams and reliability engineers don't switch contractors because of
              marketing copy. They switch because the last vendor showed up late, treated the
              symptom instead of the root cause, or disappeared when the repair failed.
            </p>
            <p className="font-semibold text-slate-900">
              BCS has built its reputation by doing the opposite.
            </p>
            <p>
              We mobilize quickly — often within 24 hours of the call. We execute in live plant
              environments when shutdowns aren't an option. Every repair is documented with
              photos, measurements, and material certifications, creating a clear record of
              exactly what was done. And when we stand behind our work, we do it with defined
              warranty terms measured in years — not vague "lifetime" promises that become
              meaningless when accountability matters.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Become Our Next Success Story
          </h2>
          <p className="text-orange-50 text-lg mb-8 max-w-2xl mx-auto">
            Join the industrial leaders who trust BCS to extend asset life, reduce downtime,
            and protect their most critical infrastructure.
          </p>
          <a
            href="mailto:info@bcs-us.com?subject=BCS%20Project%20Inquiry&body=Hi%20BCS%20Team%2C%0A%0AI%27d%20like%20to%20discuss%20a%20project%20with%20you.%0A%0AProject%20description%3A%0A%0ALocation%3A%0A%0ATimeline%3A%0A%0AThanks%2C%0A"
            data-testid="customers-cta"
            className="inline-flex items-center gap-2 bg-white hover:bg-blue-50 text-blue-900 font-semibold px-8 py-4 rounded-lg shadow-lg transition-colors"
          >
            Talk to BCS Today
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Customers;
